import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import {
  FirestoreError,
  serverTimestamp,
  Timestamp,
} from '@angular/fire/firestore';
import axios, { AxiosRequestConfig } from 'axios';
import { catchError, map, Observable, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppExceptionModel } from '../models/app-exception.model';
import { Assignment } from '../models/assignment.school.model';
import { SubmittedAssignment } from '../models/submit.assignment.school.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolAssignmentService {
  http = inject(HttpClient);
  afs: AngularFirestore = inject(AngularFirestore);
  utilsService = inject(UtilsService);

  constructor() {}

  getAssignments({
    groupId,
    gradeId,
    studentId,
    limit,
    lastRecord,
  }: {
    groupId: string;
    gradeId?: string;
    studentId?: string;
    limit?: number;
    tags?: string[];
    lastRecord?: Timestamp;
  }): Observable<Assignment[]> {
    let query: AngularFirestoreCollection<Assignment> = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('assignments', (ref) => {
        var reference = ref.where('groupId', '==', groupId);

        if (gradeId) {
          reference = reference.where('gradeId', '==', gradeId);
        }

        if (studentId) {
          reference = reference.where('studentId', '==', studentId);
        }

        reference = reference.orderBy('modified', 'desc');

        if (limit) reference = reference.limit(limit ?? 10);

        if (lastRecord) reference = reference.startAfter(lastRecord);

        return reference;
      });

    return query.get().pipe(
      take(1),
      map((changes) => {
        return changes.docs.map((a) => {
          var data: Assignment = a.data() as Assignment;
          data.id = a.id;
          return data;
        });
      }),
      catchError((error: FirestoreError) => {
        // console.log(`GradeAssignmentService :: getGradeAssignmentsByGroup: Error: ${error}`);
        return [];
      }),
    );
  }
  handleGetAssignmentsError(error: any) {
    // console.log(
    //   `GradeAssignmentService :: getGradeAssignmentsByGroup: Error: ${error?.toString()}`
    // );
    const appException: AppExceptionModel = {
      code: 'ERR_GET_ASSIGNMENTS',
      message:
        'Unable to fetch Assignments at this time. Please try again later',
      title: 'Error Fetching Assignments',
    };
    throw appException;
  }

  getAssignmentById(groupId: string, id: string): Observable<Assignment> {
    const doc = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('assignments')
      .doc(id)
      .get();
    return doc.pipe(
      take(1),
      map((doc) => {
        return { ...doc.data(), id: id } as Assignment;
      }),
    );
  }

  async create(
    groupId: string,
    assignmentId: string,
    data: Assignment,
  ): Promise<void> {
    // console.log(
    //   `AssignmentService:: create: Id: ${assignmentId}, Data:${JSON.stringify(
    //     data,
    //     null,
    //     2
    //   )}`
    // );
    try {
      return await this.afs
        .collection('groups')
        .doc(groupId)
        .collection('assignments')
        .doc(assignmentId)
        .set(
          {
            ...data,
            created: Timestamp.now(),
            modified: Timestamp.now(),
          },
          { merge: true },
        );
    } catch (error) {
      // console.log('AssignmentService:: create:', JSON.stringify(error));
      const err = {
        code: 'ERR_CREATE_ASSIGNMENT',
        message:
          'Unable to create Assignment at this time. Please try again later',
        title: 'Error Creating Assignment',
      };
      throw err;
    }
  }

  async update(
    groupId: string,
    id: string,
    assignment: Assignment,
  ): Promise<void> {
    // console.log(
    //   `AssignmentService:: update: ${id}: `,
    //   JSON.stringify(assignment, null, 2)
    // );
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('assignments')
      .doc(id)
      .update(assignment);
  }

  async getAssignmentDocument(url: string) {
    const config: AxiosRequestConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };
    return axios(url)
      .then((response) => {
        // console.debug(
        //   `AssignmentService:: getAssignmentDocument: Response: ${JSON.stringify(response.data)}`
        // );
        return response.data;
      })
      .catch((error) => {
        console.error(
          `AssignmentService:: getAssignmentDocument: Error: ${error}`,
        );
        throw error;
      });
  }

  /**
   * Get the signed URL for uploading assignment text notes
   *
   * @param inputParams
   * @returns
   */
  async getUploadAssignmentUrl(inputParams: any): Promise<any> {
    return this.utilsService
      .setAxiosHeaders(true)
      .then((headers) => {
        return axios.get<any>(
          environment['AWS_SCHOOL_ASSIGNMENTS_SIGNEDURL']!,
          {
            params: { ...inputParams },
            headers: headers,
          },
        );
      })
      .then((response) => {
        return {
          method: 'PUT',
          url: response.data.url,
          headers: {
            'Content-Type': `application/json`,
          },
        };
      });
  }

  async uploadAssignmentDocument(url: string, data: any): Promise<any> {
    try {
      return axios.put(url, data).then((response) => {
        // console.log('AssignmentService:: uploadAssignment:', response);
        return response;
      });
    } catch (error) {
      // console.log('AssignmentService:: uploadAssignment:', JSON.stringify(error));
    }
  }

  getSubmittedAssignments({
    groupId,
    assignmentId,
    studentId,
    limit,
    lastRecord,
  }: {
    groupId: string;
    assignmentId: string;
    studentId: string;
    limit?: number;
    tags?: string[];
    lastRecord?: Timestamp;
  }): Observable<SubmittedAssignment[]> {
    let query: AngularFirestoreCollection<SubmittedAssignment> = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('studentwork', (ref) => {
        var reference = ref
          .where('groupId', '==', groupId)
          .where('assignmentId', '==', assignmentId)
          .where('studentId', '==', studentId);

        reference = reference.orderBy('modified', 'desc');

        if (limit) reference = reference.limit(limit ?? 10);

        if (lastRecord) reference = reference.startAfter(lastRecord);

        return reference;
      });

    return query.get().pipe(
      take(1),
      map((changes) => {
        return changes.docs.map((a) => {
          var data: SubmittedAssignment = a.data() as SubmittedAssignment;
          data.id = a.id;
          return data;
        });
      }),
      catchError((error: FirestoreError) => {
        // console.log(`GradeAssignmentService :: getSubmittedAssignments: Error: ${error}`);
        return [];
      }),
    );
  }
  handleGetStudentWorkError(error: any) {
    // console.log(
    //   `GradeAssignmentService :: handleGetStudentWorkError: Error: ${error?.toString()}`
    // );
    const appException: AppExceptionModel = {
      code: 'ERR_GET_STUDENT_WORK',
      message:
        'Unable to fetch submitted assignments at this time. Please try again later',
      title: 'Error Fetching Submitted Assignments',
    };
    throw appException;
  }

  async submitStudentWork(data: SubmittedAssignment): Promise<void> {
    // console.log(
    //   `GradeAssignmentService :: submitStudentWork: Data: ${JSON.stringify(
    //     data
    //   )}`
    // );

    try {
      return await this.afs
        .collection('groups')
        .doc(data.groupId)
        .collection('studentwork')
        .doc(data.id)
        .set(
          {
            ...data,
            created: serverTimestamp(),
            modified: serverTimestamp(),
          },
          { merge: true },
        )
        .catch((error) => {
          console.log('Error in submitStudentWork:', error);
          throw error;
        });
    } catch (error) {
      console.log(
        'AssignmentService:: submitStudentWork:',
        JSON.stringify(error),
      );
      const err = {
        code: 'ERR_SUBMIT_ASSIGNMENT',
        message:
          'Unable to submit assignment at this time. Please try again later',
        title: 'Error Submitting Assignment',
      };
      throw err;
    }
  }

  async delete(groupId: string, id: string): Promise<void> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection<Assignment>('assignments')
      .doc(id)
      .delete();
  }

  async deleteStudentWork(groupId: string, id: string): Promise<void> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('studentwork')
      .doc(id)
      .delete();
  }
}
